import React, { useState, useEffect, useRef } from 'react';
import Iconfont from '@base/lib/Iconfont';
import cn from 'classnames';
import styles from './index.module.less';
import { useTrans } from 'base/utils/i18n';

type InputNumberProps = {
    min?: number;
    max?: number;
    value?: number | string;
    step?: number;
    precision?: number;
    onChange?: (value: string) => any;
    onBlur?: (value: string | number) => any;
    onClick?: (step: number) => void;
    className?: string;
    disabled?: boolean;
    tabIndex?: number;
};

const InputNumber: React.FC<InputNumberProps> = props => {
    const {
        min,
        max,
        value,
        step = 1,
        precision = 0,
        onChange,
        onClick,
        onBlur,
        className,
        disabled = false,
        tabIndex = 0
    } = props;

    const [realtime, setRealtime] = useState<number | string>('');
    const editingRef = useRef<boolean>(false);
    const { t } = useTrans();

    const format = (newValue: number | string): number | string => {
        let _value: number | string = Number(newValue);

        // 最大最小值
        if (typeof min !== 'undefined') {
            _value = Math.max(min, _value);
        }
        if (typeof max !== 'undefined') {
            _value = Math.min(max, _value);
        }

        // 精度
        if (precision <= 0) {
            _value = Math.floor(_value);
        } else {
            const multiples = Number(`1${new Array(precision).fill(0).join('')}`);
            _value = (Math.round(_value * multiples) / multiples).toFixed(precision);
        }
        setRealtime(_value);
        return _value;
    };

    const handleUpdate = (newValue: number | string) => {
        typeof onChange === 'function' && onChange(String(newValue));
    };

    const handleChange = (newValue: number | string) => {
        let _value = String(newValue).replace(/[^\d.]/g, '').replace(/^(\d*\.)(\d*).*$/, '$1$2');
        _value = (_value == '0' || !_value) ? '1' : _value
        setRealtime(_value);
    };

    const handleBlur = (newValue: string) => {
        editingRef.current = false;
        let _value: string | number = newValue.replace(/[^\d.]/g, '').replace(/^(\d*\.)(\d*).*$/, '$1$2') || '1';
        const arr = _value.split('.');
        const integer = arr[0] || 0;
        if (precision <= 0) {
            _value = format(integer);
        } else {
            const decimal = arr[1] ? arr[1].substr(0, precision) : 0;
            _value = format(`${integer}.${decimal}`);
        }
        if (String(value) !== String(_value)) {
            handleUpdate(_value);
            onBlur?.(_value)
        }
    };

    const handleStep = (step: number) => {
        handleUpdate(format(Number(value) + step));
        onClick?.(step)
    };

    useEffect(() => {
        if (!editingRef.current) {
            // 初始化和父组件值改变时转换格式
            const formated = format(value || '');
            setRealtime(formated);
            if (String(value) !== String(formated)) {
                handleUpdate(formated);
            }
        }
    }, [value]);

    return (
        <div className={cn(className, styles['input-number'])}>
            <button
                tabIndex={tabIndex}
                className="operator minus"
                onClick={(e) => {
                    e.stopPropagation()
                    handleStep(0 - step)
                }}
                disabled={(typeof min !== 'undefined' && Number(value) <= min) || disabled}
            >
                <Iconfont className="icon-minus"/>
                <span className="visually-hidden">{t('web_form_number_minus')}</span>
            </button>
            <label className="visually-hidden" htmlFor="numberOfVariants">
                {t('web_product_number_label')}
            </label>
            <input
                tabIndex={tabIndex}
                id="numberOfVariants"
                type="text"
                className="input"
                value={realtime}
                onChange={e => handleChange(e.target.value)}
                onFocus={() => editingRef.current = true}
                onBlur={e => handleBlur(e.target.value)}
                disabled={disabled}
            />
            <button
                tabIndex={tabIndex}
                className="operator plus"
                onClick={(e) => {
                    e.stopPropagation()
                    handleStep(step)
                }}
                disabled={(typeof max !== 'undefined' && Number(value) >= max) || disabled}
            >
                <Iconfont className="icon-add"/>
                <span className="visually-hidden">{t('web_form_number_plus')}</span>
            </button>
        </div>
    )
};

export default InputNumber;